import React, { Fragment, useState } from 'react'
import styled, { withTheme } from 'styled-components'
import PropTypes from 'prop-types'
import { bindActionCreators, compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import CardPriceAmount from '../../components/price/CardPriceAmount'
import Link from '../../components/router/Link'
import { desktop, mobile, VIEWPORT_DESKTOP, VIEWPORT_MOBILE } from '../../styles/media'
import translate from '../../helper/translate'
import { priceSourceOptions } from '../../data'
import { priceSourceDefaults } from '../../../config'
import { CARD_MARKET, priceSourceParents, TCG_PLAYER } from '../../data/priceSources'
import Icon from '../../components/general/Icon'
import { formatRgb, hexToRgb } from '../../helper/color'
import { createAffiliateClick } from '../../state/actionCreators/app'
import { copyDeckListToClipBoard, getMissingCards, getTcgPlayerMassEntryLink } from '../../helper/deck'
import Modal from '../../components/router/Modal'
import Message from '../../components/form/Message'
import H2 from '../../components/general/H2'
import BetterButton from '../../components/button/BetterButton'

const BuyLinks = ({ createAffiliateClick, deck, deckCards, externalLinks, location, lowestPrices, mobileShowAll, presetPriceSource, printId, settings, t, theme, userLanguage, viewport }) => {
	const cardNameLanguage = settings['app.cardNameLanguage']
	const priceSourceDefault = priceSourceDefaults[cardNameLanguage]
	const priceSource = Number(settings['app.priceSource']) || CARD_MARKET
	const dark = theme.id === 'dark'

	const [showCardmarketModal, setShowCardmarketModal] = useState(false)

	const handleShowCardmarketModal = () => {
		setShowCardmarketModal(true)

		const missingCards = getMissingCards(deckCards)
		copyDeckListToClipBoard(missingCards, cardNameLanguage, t)
	}

	return (
		<StyledBuyLinks mobileShowAll={mobileShowAll}>
			<BuyLinksSeparator>
				<span>
					<Icon name="shopping-cart" />
					{deck ? t('general.affiliateDeckBuyHeading') : t('general.affiliateBuyHeading')}
				</span>
			</BuyLinksSeparator>

			<BuyLinksList>
				{priceSourceOptions(priceSourceDefault)
					.filter(item => item.affiliate && (!presetPriceSource || priceSourceParents[presetPriceSource] === Number(item.value)))
					.map((item) => {
						const image = (
							<img
								srcSet={dark ? `${item.logoWhite1x} 1x, ${item.logoWhite2x} 2x, ${item.logoWhite3x} 3x` : `${item.logo1x} 1x, ${item.logo2x} 2x, ${item.logo3x} 3x`}
								src={dark ? item.logoWhite1x : item.logo1x}
								alt={item.name}
							/>
						)

						let link = item.buildLink(externalLinks ? externalLinks[priceSourceParents[item.value]] : null, userLanguage, settings)

						if (deck && Number(item.value) === TCG_PLAYER) {
							link = item.buildLink(getTcgPlayerMassEntryLink(deckCards), userLanguage, settings)
						} else if (deck && Number(item.value) === CARD_MARKET) {
							return (
								<Fragment key={item.value}>
									<BuyLinksListItem
										onClick={handleShowCardmarketModal}
										priceSource={Number(item.value)}
										color={item.color}
										hasPriceAmount={false}
									>
										<BuyLinksListItemLogo priceSource={Number(item.value)}>
											{image}
										</BuyLinksListItemLogo>
									</BuyLinksListItem>

									<Modal
										visible={showCardmarketModal}
										onClickAway={() => setShowCardmarketModal(false)}
										onClose={() => setShowCardmarketModal(false)}
									>
										<H2>{t('general.cardmarketBuyDeckDescription1')}</H2>

										<Message success icon="check-circle">
											{t('general.deckListCopied')}
										</Message>

										<HowToOl>
											<li>
												{t('general.cardmarketBuyDeckDescription2')}<br />
												<small>{t('general.cardmarketBuyDeckDescription3')}</small>
											</li>
											<li>
												{t('general.cardmarketBuyDeckDescription4')}
											</li>
											<li>
												{t('general.cardmarketBuyDeckDescription5')}
											</li>
											<li>
												{t('general.cardmarketBuyDeckDescription6')}
											</li>
										</HowToOl>

										<BetterButton
											external
											externalNoIcon
											to={item.buildLink('/YuGiOh/Wants', userLanguage)}
										>
											{t('general.cardmarketBuyDeckDescription7')}
										</BetterButton>
									</Modal>
								</Fragment>
							)
						}

						return (
							<BuyLinksListItem
								external
								externalNoIcon
								to={link}
								onClick={() => createAffiliateClick(item.value, printId, location.pathname)}
								priceSource={Number(item.value)}
								color={item.color}
								disabled={!link}
								hasPriceAmount={lowestPrices && (viewport === VIEWPORT_DESKTOP || mobileShowAll)}
								key={item.value}
							>
								<BuyLinksListItemLogo priceSource={Number(item.value)}>
									{image}
								</BuyLinksListItemLogo>

								{lowestPrices && (viewport === VIEWPORT_DESKTOP || mobileShowAll) && (
									<CardPriceAmount
										currency={item.currency}
										amount={lowestPrices[item.value]}
										small={viewport === VIEWPORT_MOBILE}
									/>
								)}
							</BuyLinksListItem>
						)
					})}
			</BuyLinksList>
		</StyledBuyLinks>
	)
}

BuyLinks.propTypes = {
	createAffiliateClick: PropTypes.func.isRequired,
	deck: PropTypes.bool,
	deckCards: PropTypes.array,
	externalLinks: PropTypes.object,
	location: PropTypes.object.isRequired,
	lowestPrices: PropTypes.object,
	mobileShowAll: PropTypes.bool,
	presetPriceSource: PropTypes.number,
	printId: PropTypes.number,
	settings: PropTypes.object.isRequired,
	t: PropTypes.func.isRequired,
	theme: PropTypes.object.isRequired,
	userLanguage: PropTypes.string.isRequired,
	viewport: PropTypes.string.isRequired
}

BuyLinks.defaultProps = {
	deck: false,
	deckCards: null,
	externalLinks: null,
	lowestPrices: null,
	mobileShowAll: false,
	presetPriceSource: null,
	printId: null
}

export const StyledBuyLinks = styled.div`
	display: flex;
	flex-direction: column;

	${props => props.mobileShowAll && `
		margin-top: 0.5rem;
	`}
`

export const BuyLinksSeparator = styled.div`
	margin: 0 auto 0.25rem;
	position: relative;

	span {
		color: ${props => props.theme.textLight};
		font-size: 0.6rem;
		font-weight: 700;
		text-transform: uppercase;
		white-space: nowrap;

		align-items: center;
		display: flex;

		svg {
			height: 12px;
			width: 12px;
			margin-right: 0.25rem;
		}
	}

	${desktop`
		margin: 0 auto 0.5rem;

		span {
			font-size: 0.8rem;

			svg {
				height: 16px;
				width: 16px;
			}
		}
	`}
`

const BuyLinksList = styled.div`
	display: flex;
	gap: 1rem;

	${mobile`
		gap: 0.5rem;
	`}
`

const BuyLinksListItemLogo = styled.div`
	img {
		width: 100px;
	}

	${mobile`
		img {
			width: 80px;
			max-width: 100%;
		}
	`}
`

const BuyLinksListItemName = styled.div`
	font-weight: 500;
`

const BuyLinksListItem = styled(Link)`
	background: ${props => props.theme.background};
    border: 1px solid ${props => formatRgb(hexToRgb(props.theme[props.color]), 0.5)};
	border-radius: 8px;
	height: 38px;
	padding: 0 0.5rem;
	text-decoration: none;
	transition: 0.1s;

	align-items: center;
	display: flex;
	flex: 1;

	${props => props.hasPriceAmount ? `
		> :last-child {
			margin-left: auto;
		}

		${BuyLinksListItemLogo} {
			margin-right: 0.75rem;
		}
	` : `
		justify-content: center;
	`}

	${props => props.disabled && `
		opacity: 0.5;
	`}

	${desktop`
		&:hover {
			border-color: ${props => props.theme[props.color]};
			box-shadow: 0 0 0 1px ${props => props.theme[props.color]};

			${BuyLinksListItemName} {
				text-decoration: underline;
			}
		}
	`}

	${mobile`
		border-width: 2px;
		padding: 0 0.35rem;
	`}
`

const HowToOl = styled.ol`
	padding: 0 0 0 1.1rem;
	margin-bottom: 2.5rem;

	li {
		color: ${props => props.theme.text};
		line-height: 1.4;
		margin-bottom: 1rem;
		padding-left: 0.5rem;

		&::marker {
			color: ${props => props.theme.textLight};
			font-weight: 500;
		}

		small {
			color: ${props => props.theme.textLight};
			line-height: 1;
		}
	}
`

const enhance = compose(
	connect(state => ({
		settings: state.persist.user.settings,
		userLanguage: state.persist.language,
		viewport: state.app.viewport
	}), dispatch => bindActionCreators({
		createAffiliateClick
	}, dispatch)),
	translate('general'),
	withRouter,
	withTheme
)

export default enhance(BuyLinks)
